/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext } from 'react';
import { isEmpty, get, forIn } from 'lodash';
import { Card, Row, Col, Tab, Nav } from 'react-bootstrap';
import StatisticsCards from 'components/dashboards/saas/stats-cards/StatisticsCards';
import BillingHeader from 'components/app/e-commerce/billing/BillingHeader';
import WidgetSectionTitle from '../../../widgets/CustomWidgetSectionTitle';
import useAPI from 'hooks/useApi';
import { getEnvironmentologyEvaluation } from 'api/endpoints/EnvironmentalServices';
import { getHydrologicalEvaluation } from 'api/endpoints/HydrologicalServices';
import { getOverallEvaluation } from 'api/endpoints/TerrestrialServices';
import { getTerrestrialEvaluation } from 'api/endpoints/TerrestrialServices';
import AppContext from 'context/Context';
import locationIcon from 'assets/img/icons/land.svg';
import waterIcon from 'assets/img/icons/water.svg';
import soilIcon from 'assets/img/icons/soil_param.svg';
import { getSoilParameter } from 'api/endpoints/PedologicalServices';
import FalconCardHeader from 'components/common/FalconCardHeader';
import LmsStats from '../lms/lms-stats/LmsStats';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOutletContext, useParams } from 'react-router-dom';
import ErrorNotFound from 'components/errors/ErrorNotFound';
import LandUseAndLandCoverChart from './charts/LandUseAndLandCoverChart';
import StatsSkeleton from './skeleton/StatsSkeleton';
import StatiticsCardsSkeleton from './skeleton/StatiticsCardsSkeleton';
import CardSkeleton from '../../common/skeleton/CardSkeleton';
import InsightsSkeleton from './skeleton/InsightsSkeleton';
import SeedSuggestionSkeleton from './skeleton/SeedSuggestionSkeleton';
import ForestCoverSkeleton from './skeleton/ForestCoverSkeleton';
import LandCoverMap from './maps/LandCoverMap';
import TreeCoverMap from './maps/TreeCoverMap';
import LeafletMap from 'components/common/map/LeafletMap';
import SlopeMaps from './maps/SlopeMaps';
import SeedSuggestion from './SeedSuggestions';
import ForestCover from './ForestCover';
import TreeCoverChart from './charts/TreeCoverChart';
import PrecipitationChart from './charts/PrecipitationChart';
import EnvironmentParameters from './EnvironmentParameters';
import SoilMaps from './maps/SoilMaps';

const Dashboard = () => {
  const { setConfig } = useContext(AppContext);
  const { cluster } = useParams();
  const [showErrorPage, setshowErrorPage] = useState(false);
  const { setApisLoading } = useOutletContext();

  const getEnvironmentologyEvaluationAPI = useAPI(
    getEnvironmentologyEvaluation
  );
  const getHydrologicalEvaluationAPI = useAPI(getHydrologicalEvaluation);
  const getOverAllEvaluationAPI = useAPI(getOverallEvaluation);
  const getTerrestrialEvaluationAPI = useAPI(getTerrestrialEvaluation);
  const getSoilParameterAPI = useAPI(getSoilParameter);

  const [environmentologyEvaluationData, setEnvironmentologyEvaluationData] =
    useState({});
  const [hydrologicalEvaluationData, setHydrologicalEvaluationData] = useState(
    {}
  );
  const [overAllEvaluationData, setOverAllEvaluationData] = useState({});
  const [terrestrialEvaluationData, setTerrestrialEvaluationData] = useState(
    {}
  );
  const [soilParameterData, setSoilParameterData] = useState({});

  const apiHooks = [
    getOverAllEvaluationAPI,
    getTerrestrialEvaluationAPI,
    getHydrologicalEvaluationAPI,
    getEnvironmentologyEvaluationAPI,
    getSoilParameterAPI
  ];

  const performSequentialAPICalls = async currentCluster => {
    setApisLoading(true);
    for (let i = 0; i < apiHooks.length; i++) {
      await apiHooks[i].request(currentCluster);
    }
    setApisLoading(false);
  };

  const resetApiStates = () => {
    for (let i = 0; i < apiHooks.length; i++) {
      apiHooks[i].setLoading(true);
    }
  };

  useEffect(() => {
    setConfig('cluster', cluster);
    setshowErrorPage(false);

    if (cluster) {
      resetApiStates(); // Reset loading and data for all APIs

      performSequentialAPICalls(cluster);
    }
  }, [cluster]);

  useEffect(() => {
    if (
      !isEmpty(getOverAllEvaluationAPI.error) ||
      !isEmpty(getEnvironmentologyEvaluationAPI.error) ||
      !isEmpty(getHydrologicalEvaluationAPI.error) ||
      !isEmpty(getSoilParameterAPI.error) ||
      !isEmpty(getTerrestrialEvaluationAPI.error)
    ) {
      setshowErrorPage(true);
    }
  }, [
    getOverAllEvaluationAPI.error,
    getEnvironmentologyEvaluationAPI.error,
    getHydrologicalEvaluationAPI.error,
    getSoilParameterAPI.error,
    getTerrestrialEvaluationAPI.error
  ]);

  useEffect(() => {
    if (!isEmpty(getEnvironmentologyEvaluationAPI.data)) {
      setEnvironmentologyEvaluationData(
        get(getEnvironmentologyEvaluationAPI.data, 'results', {})
      );
    }
  }, [getEnvironmentologyEvaluationAPI.data]);

  useEffect(() => {
    if (!isEmpty(getHydrologicalEvaluationAPI.data)) {
      setHydrologicalEvaluationData(
        get(getHydrologicalEvaluationAPI.data, 'results', {})
      );
    }
  }, [getHydrologicalEvaluationAPI.data]);

  useEffect(() => {
    if (!isEmpty(getOverAllEvaluationAPI.data)) {
      setOverAllEvaluationData(
        get(getOverAllEvaluationAPI.data, 'results', {})
      );
      const reportUrl = get(
        getOverAllEvaluationAPI.data,
        'results.assessment_report',
        ''
      );
      setConfig('pdfReportUrl', reportUrl);
    }
  }, [getOverAllEvaluationAPI.data]);

  useEffect(() => {
    if (!isEmpty(getSoilParameterAPI.data)) {
      setSoilParameterData(get(getSoilParameterAPI.data, 'results', {}));
    }
  }, [getSoilParameterAPI.data]);

  useEffect(() => {
    if (!isEmpty(getTerrestrialEvaluationAPI.data)) {
      setTerrestrialEvaluationData(
        get(getTerrestrialEvaluationAPI.data, 'results', {})
      );
    }
  }, [getTerrestrialEvaluationAPI.data]);

  const startDate = get(overAllEvaluationData, 'overall_score');
  const insights = get(overAllEvaluationData, 'insights', '');
  const projectDetails = get(overAllEvaluationData, 'asessment_details');

  const seedSuggestion = get(terrestrialEvaluationData, 'seed_suggestion');

  const seedSuggestionItems = [];
  if (!isEmpty(seedSuggestion)) {
    const colors = ['primary', 'warning', 'success', 'info', 'secondary'];
    seedSuggestion.map((data, index) => {
      const random = Math.floor(Math.random() * colors.length);
      seedSuggestionItems.push({
        title: get(data, 'seed', ''),
        text: get(data, 'desc', ''),
        score: get(data, 'score', ''),
        color: colors[random]
      });
    });
  }

  const forestCoverage = get(terrestrialEvaluationData, 'forest_coverage');
  const forestCover = [];
  let areaName = '';
  if (!isEmpty(forestCoverage)) {
    forIn(forestCoverage, (value, key) => {
      const color = ['primary', 'secondary'];
      if (key !== 'total_area') {
        let count = 0;
        forIn(value, (value, key) => {
          forestCover.push({
            id: count,
            value,
            name: key,
            color: color[count]
          });
          count++;
        });
      } else {
        areaName = value;
      }
    });
  }

  const environmentologyObservation = get(
    environmentologyEvaluationData,
    'environmentology_observation'
  );

  const waterStress = get(
    hydrologicalEvaluationData,
    'hydrological_observation.water_stress'
  );

  const surfaceWaterExtend = get(
    hydrologicalEvaluationData,
    'hydrological_observation.surface_water_extent'
  );

  const precipitation = get(
    hydrologicalEvaluationData,
    'hydrological_observation.precipitation'
  );

  const agriculturalDrought = get(
    hydrologicalEvaluationData,
    'hydrological_observation.agricultural_drought'
  );

  const metrologicalDrought = get(
    hydrologicalEvaluationData,
    'hydrological_observation.metrological_drought'
  );
  /* const audienceChart = {};
  if (!isEmpty(waterStress)) {
    waterStress.map(data => {
      audienceChart[data.month] = [data.value];
    });
  } */

  const terrestrialObservation = get(
    terrestrialEvaluationData,
    'terrestrial_observation',
    {}
  );
  const tarrestrialMapData = [];
  const landCoverData = [];
  const deforestationData = [];
  let vegetationMapData = {};
  let elevationMapData = {};
  let mangroveForest = {};
  const deforestationMetricsChartData = get(
    terrestrialObservation,
    'deforestation_metrics',
    {}
  );
  const landCoverChartData = get(terrestrialObservation, 'lclu_metrics', {});
  forIn(terrestrialObservation, (value, key) => {
    if (key === 'vegetation') {
      vegetationMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key
      };
    } else if (key === 'elevation') {
      elevationMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key
      };
    } else if (key === 'slope' || key === 'slope_direction') {
      tarrestrialMapData.push({
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key
      });
    } else if (key === 'lclu') {
      value.map((landData, index) => {
        landCoverData.push({
          title: get(landData, 'label', ''),
          tifUrl: get(landData, 'url', ''),
          info: get(landData, 'info', ''),
          key: `${key}_${index}`
        });
      });
    } else if (key === 'deforestation') {
      value.map((landData, index) => {
        deforestationData.push({
          title: get(landData, 'label', ''),
          tifUrl: get(landData, 'url', ''),
          info: get(landData, 'info', ''),
          key: `${key}_${index}`
        });
      });
    } else if (key === 'mangrove_forest') {
      mangroveForest = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key
      };
    }
  });

  const locationCoordinates = get(terrestrialEvaluationData, 'aoi', []).map(
    row => [row[1], row[0]]
  );

  const mapData = {
    coordinates: locationCoordinates,
    mapData: tarrestrialMapData
  };

  const coordinates = get(hydrologicalEvaluationData, 'aoi', []).map(row => [
    row[1],
    row[0]
  ]);

  const environmentCoordinates = get(
    environmentologyEvaluationData,
    'aoi',
    []
  ).map(row => [row[1], row[0]]);

  const soilMapData = [];
  let soilNutrientMapData = {};
  let soilNutrientRetentionMapData = {};
  let soilTextureMapData = {};
  let soilOrganicCarbonMapData = {};
  forIn(get(soilParameterData, 'pedological_observation', {}), (value, key) => {
    if (key === 'nutrient_availability') {
      soilNutrientMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key,
        coordinates: get(soilParameterData, 'aoi', []).map(row => [
          row[1],
          row[0]
        ])
      };
    } else if (key === 'nutrient_retention_capability') {
      soilNutrientRetentionMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key,
        coordinates: get(soilParameterData, 'aoi', []).map(row => [
          row[1],
          row[0]
        ])
      };
    } else if (key === 'organic_carbon') {
      soilOrganicCarbonMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key,
        coordinates: get(soilParameterData, 'aoi', []).map(row => [
          row[1],
          row[0]
        ])
      };
    } else if (key === 'soil_texture') {
      soilTextureMapData = {
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key,
        coordinates: get(soilParameterData, 'aoi', []).map(row => [
          row[1],
          row[0]
        ])
      };
    } else {
      soilMapData.push({
        title: get(value[0], 'label', ''),
        tifUrl: get(value[0], 'url', ''),
        info: get(value[0], 'info', ''),
        key,
        coordinates: get(soilParameterData, 'aoi', []).map(row => [
          row[1],
          row[0]
        ])
      });
    }
  });

  const defaultActiveKey = [
    'precipitation',
    'water_stress',
    'surface_water_extent',
    'agricultural_drought',
    'metrological_drought'
  ];
  const waterParameterMapData = !isEmpty(waterStress)
    ? {
        title: get(waterStress[0], 'label', ''),
        tifUrl: get(waterStress[0], 'url', ''),
        info: get(waterStress[0], 'info', ''),
        key: defaultActiveKey[1]
      }
    : {};
  const surfaceWaterExtendMapData = !isEmpty(surfaceWaterExtend)
    ? {
        title: get(surfaceWaterExtend[0], 'label', ''),
        tifUrl: get(surfaceWaterExtend[0], 'url', ''),
        info: get(surfaceWaterExtend[0], 'info', ''),
        key: defaultActiveKey[2]
      }
    : {};
  const agriculturalDroughtMapData = !isEmpty(agriculturalDrought)
    ? {
        title: get(agriculturalDrought[0], 'label', ''),
        tifUrl: get(agriculturalDrought[0], 'url', ''),
        info: get(agriculturalDrought[0], 'info', ''),
        key: defaultActiveKey[3]
      }
    : {};
  const metrologicalDroughtMapData = !isEmpty(metrologicalDrought)
    ? {
        title: get(metrologicalDrought[0], 'label', ''),
        tifUrl: get(metrologicalDrought[0], 'url', ''),
        info: get(metrologicalDrought[0], 'info', ''),
        key: defaultActiveKey[4]
      }
    : {};

  const chartValue = get(precipitation, 'data', []);
  const xAxis = get(precipitation, 'date', []);
  const precipitationSeries = get(precipitation, 'series', []);

  return (
    <>
      {showErrorPage ? (
        <ErrorNotFound />
      ) : (
        <>
          <Row className="g-3 mb-3 mt-1">
            <Col lg={7} className="mt-0">
              <Row>
                <Col lg={12}>
                  {isEmpty(projectDetails) ||
                  getOverAllEvaluationAPI.loading ? (
                    <StatsSkeleton />
                  ) : (
                    <LmsStats data={projectDetails} />
                  )}
                </Col>
                <Col lg={12}>
                  {isEmpty(startDate) || getOverAllEvaluationAPI.loading ? (
                    <StatiticsCardsSkeleton count={4} />
                  ) : (
                    <StatisticsCards statsData={startDate} />
                  )}
                </Col>
              </Row>
              <Row></Row>
            </Col>
            <Col lg={5} className="mt-0">
              {isEmpty(insights) || getOverAllEvaluationAPI.loading ? (
                <InsightsSkeleton />
              ) : (
                <BillingHeader title="Insights" insights={insights} />
              )}
            </Col>
          </Row>
          <WidgetSectionTitle
            icon={locationIcon}
            title="Location Parameters"
            transform="shrink-4"
            className="mb-4 mt-4 align-items-center"
          />
          <Row className="g-3 mb-3">
            <Col lg={6}>
              {isEmpty(vegetationMapData) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className=" bg-light h-100">
                  <FalconCardHeader
                    title={vegetationMapData.title}
                    className=""
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: locationCoordinates,
                        mapData: vegetationMapData
                      }}
                      coordinates={coordinates}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {vegetationMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(elevationMapData) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={elevationMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: locationCoordinates,
                        mapData: elevationMapData
                      }}
                      coordinates={coordinates}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {elevationMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col xxl={6} lg={8}>
              {isEmpty(terrestrialObservation) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <SlopeMaps data={mapData} className="h-100" />
              )}
            </Col>
            {/* <Col xxl={6} lg={4}> */}
            {/* <Row className="mb-3">
                <Col>
                  {getTerrestrialEvaluationAPI.loading ? (
                    <SeedSuggestionSkeleton />
                  ) : (
                    <SeedSuggestion data={[...seedSuggestionItems]} />
                  )}
                </Col>
              </Row> */}

            <Col xxl={6} lg={4}>
              {isEmpty(forestCoverage) ||
              getTerrestrialEvaluationAPI.loading ? (
                <ForestCoverSkeleton />
              ) : (
                <ForestCover
                  data={forestCover}
                  areaName={areaName}
                  radius={['100%', '70%']}
                />
              )}
            </Col>

            <Col lg={6}>
              {isEmpty(landCoverData) ||
              isEmpty(locationCoordinates) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <LandCoverMap
                  landCoverData={landCoverData}
                  coordinates={locationCoordinates}
                />
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(landCoverChartData) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton footer={false} minHeight="23.75rem" />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={landCoverChartData?.label}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 px-3" dir="ltr">
                    <p className="units-text">(in ha)</p>
                    <LandUseAndLandCoverChart
                      landCoverChartData={landCoverChartData}
                    />
                  </Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={6}>
              {isEmpty(deforestationData) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <TreeCoverMap
                  deforestationData={deforestationData}
                  coordinates={locationCoordinates}
                />
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(deforestationMetricsChartData) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton footer={false} />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={deforestationMetricsChartData?.label}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 px-3" dir="ltr">
                    <div className="chart-legend-container mb-3 px-2">
                      <div className="chart-legend-item">
                        <FontAwesomeIcon
                          icon="circle"
                          className={`text-primary fs--2 me-2`}
                        />
                        <span>Tree Cover</span>
                      </div>
                      <p className="units-text">(in ha)</p>
                    </div>
                    <TreeCoverChart
                      data={deforestationMetricsChartData.data}
                      xAxis={deforestationMetricsChartData.series}
                    />
                  </Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={12}>
              {isEmpty(mangroveForest) ||
              getTerrestrialEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={mangroveForest.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{ coordinates, mapData: mangroveForest }}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {mangroveForest.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
          </Row>
          <WidgetSectionTitle
            icon={waterIcon}
            title="Water Parameters"
            transform="shrink-4"
            className="mb-4 mt-4 align-items-center"
          />
          <Row className="g-3 mb-3">
            <Col lg={6}>
              {isEmpty(chartValue) || getHydrologicalEvaluationAPI.loading ? (
                <CardSkeleton height="21.25rem" />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={precipitation?.label}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 pb-0" dir="ltr">
                    <div className="chart-legend-container">
                      <div className="chart-legend-item">
                        <FontAwesomeIcon
                          icon="circle"
                          className={`text-primary fs--2 me-2`}
                        />
                        <span>{precipitationSeries[0]}</span>
                        <FontAwesomeIcon
                          icon="circle"
                          className={`text-info fs--2 me-2`}
                        />
                        <span>{precipitationSeries[1]}</span>
                      </div>
                      <p className="units-text">(in mm)</p>
                    </div>
                    <PrecipitationChart
                      data={chartValue}
                      xAxis={xAxis}
                      precipitationSeries={precipitationSeries}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">{precipitation.info}</h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(waterParameterMapData) ||
              getHydrologicalEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={waterParameterMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{ coordinates, mapData: waterParameterMapData }}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {waterParameterMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={12}>
              {isEmpty(surfaceWaterExtendMapData) ||
              getHydrologicalEvaluationAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={surfaceWaterExtendMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates,
                        mapData: surfaceWaterExtendMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {surfaceWaterExtendMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(agriculturalDroughtMapData) ||
              getHydrologicalEvaluationAPI.loading ? (
                <CardSkeleton footer={false} />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={agriculturalDroughtMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0 overflow-hidden" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates,
                        mapData: agriculturalDroughtMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  {agriculturalDroughtMapData.info && (
                    <Card.Footer className="bg-white py-2 card-footer-style">
                      <h5 className="mb-0 infrence-info">
                        {agriculturalDroughtMapData.info}
                      </h5>
                    </Card.Footer>
                  )}
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(metrologicalDroughtMapData) ||
              getHydrologicalEvaluationAPI.loading ? (
                <CardSkeleton footer={false} />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={metrologicalDroughtMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0 overflow-hidden" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates,
                        mapData: metrologicalDroughtMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '400px' }}
                    />
                  </Card.Body>
                  {metrologicalDroughtMapData.info && (
                    <Card.Footer className="bg-white py-2 card-footer-style">
                      <h5 className="mb-0 infrence-info">
                        {metrologicalDroughtMapData.info}
                      </h5>
                    </Card.Footer>
                  )}
                </Card>
              )}
            </Col>
          </Row>
          <EnvironmentParameters
            getEnvironmentologyEvaluationAPI={getEnvironmentologyEvaluationAPI}
            environmentologyObservation={environmentologyObservation}
            coordinates={environmentCoordinates}
          />
          <WidgetSectionTitle
            icon={soilIcon}
            title="Soil Parameters"
            transform="shrink-4"
            className="mb-4 mt-4 align-items-center"
          />
          <Row className="g-3 mb-3">
            <Col lg={6}>
              {isEmpty(soilNutrientMapData) || getSoilParameterAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={soilNutrientMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: soilNutrientMapData.coordinates,
                        mapData: soilNutrientMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '410px' }}
                      key={`${soilNutrientMapData.key}-land-locations`}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {soilNutrientMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(soilNutrientRetentionMapData) ||
              getSoilParameterAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={soilNutrientRetentionMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: soilNutrientRetentionMapData.coordinates,
                        mapData: soilNutrientRetentionMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '410px' }}
                      key={`${soilNutrientRetentionMapData.key}--land-locations`}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {soilNutrientRetentionMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col lg={6}>
              {isEmpty(soilOrganicCarbonMapData) ||
              getSoilParameterAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={soilOrganicCarbonMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: soilOrganicCarbonMapData.coordinates,
                        mapData: soilOrganicCarbonMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '410px' }}
                      key={`${soilOrganicCarbonMapData.key}-land-locations`}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {soilOrganicCarbonMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
            <Col lg={6}>
              {isEmpty(soilTextureMapData) || getSoilParameterAPI.loading ? (
                <CardSkeleton />
              ) : (
                <Card className="h-100">
                  <FalconCardHeader
                    title={soilTextureMapData.title}
                    titleTag="h5"
                    light={true}
                  />
                  <Card.Body className="h-100 p-0" dir="ltr">
                    <LeafletMap
                      data={{
                        coordinates: soilTextureMapData.coordinates,
                        mapData: soilTextureMapData
                      }}
                      className="h-100 bg-white"
                      style={{ minHeight: '410px' }}
                      key={`${soilTextureMapData.key}--land-locations`}
                    />
                  </Card.Body>
                  <Card.Footer className="bg-white py-2 card-footer-style">
                    <h5 className="mb-0 infrence-info">
                      {soilTextureMapData.info}
                    </h5>
                  </Card.Footer>
                </Card>
              )}
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col lg={12}>
              {isEmpty(soilMapData) || getSoilParameterAPI.loading ? (
                <CardSkeleton />
              ) : (
                <SoilMaps data={soilMapData} className="mb-3" />
              )}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default Dashboard;
